// src/pages/enterprise-ai/agentBuilderIssues.js
//
// Issue rules for the Agent Builder page.
//
// ISSUE_RULES is one array of pure functions. Each function tests exactly one
// state and returns either null or an issue object:
//
//   (state, ctx) => null | { code, severity, state, why }
//
// Each function also carries its own `code` and `severity` as properties so the
// legend and the pip colours can be driven off the array itself rather than a
// parallel table that can drift.
//
//   ctx = { agent, toolsByName, artifactsByName, exitTargets, reachable }
//
// Pure means pure. No state is read from outside the arguments, nothing is
// mutated, nothing is fetched. Same inputs, same issues, every time.
//
// severity: 'error' blocks a sane deploy. 'drift' is survivable but wrong.
// Both strings are shown to the user verbatim.

// ---------------------------------------------------------------------------
// Context builder
// ---------------------------------------------------------------------------

// A state's allowedExits may legitimately name a terminal label from
// completedStates or escalatedStates rather than another state. Those are real
// targets, so they count as existing and must not be reported as dead exits.
export const buildIssueContext = (agent, toolCatalog, artifactCatalog) => {
  const states = (agent && Array.isArray(agent.states) ? agent.states : []);

  const exitTargets = new Set();
  states.forEach((s) => exitTargets.add(s.name));
  ((agent && agent.completedStates) || []).forEach((n) => exitTargets.add(n));
  ((agent && agent.escalatedStates) || []).forEach((n) => exitTargets.add(n));

  // Every state named by some other state's allowedExits, plus the initial
  // state, which is reachable by definition.
  const reachable = new Set();
  if (agent && agent.initialState) reachable.add(agent.initialState);
  states.forEach((s) => {
    (s.allowedExits || []).forEach((target) => {
      // A self-loop does not make a state reachable from anywhere else.
      if (target !== s.name) reachable.add(target);
    });
  });

  return {
    agent,
    toolsByName: Object.fromEntries((toolCatalog || []).map((t) => [t.name, t])),
    artifactsByName: Object.fromEntries((artifactCatalog || []).map((a) => [a.name, a])),
    exitTargets,
    reachable,
  };
};

const issue = (fn, state, why) => ({ code: fn.code, severity: fn.severity, state: state.name, why });

const list = (arr) => (arr || []).join(', ');

// ---------------------------------------------------------------------------
// The rules
// ---------------------------------------------------------------------------

const noPrompt = (state) => {
  if (state.prompt && String(state.prompt).trim()) return null;
  return issue(
    noPrompt,
    state,
    'This state has no prompt. The model enters it with no instruction at all and will improvise its way through the turn, so behaviour here is whatever the base model feels like doing that day.'
  );
};
noPrompt.code = 'NO_PROMPT';
noPrompt.severity = 'error';

const haltNoAsk = (state) => {
  const noExits = !state.allowedExits || state.allowedExits.length === 0;
  const noArtifact = !state.artifact;
  if (!(state.haltTurn === true && noExits && noArtifact)) return null;
  return issue(
    haltNoAsk,
    state,
    'This state halts and waits for the user, but it has no exits to move to and no artifact to produce. The conversation stops here permanently with nothing to show for it. Give it an exit, an artifact, or stop halting.'
  );
};
haltNoAsk.code = 'HALT_NO_ASK';
haltNoAsk.severity = 'error';

const deadExit = (state, ctx) => {
  const dead = (state.allowedExits || []).filter((target) => !ctx.exitTargets.has(target));
  if (dead.length === 0) return null;
  return issue(
    deadExit,
    state,
    'This state can exit to ' + list(dead) + ', which is not a state on this agent and is not a completed or escalated label either. At runtime that exit resolves to nothing and the agent stalls mid conversation.'
  );
};
deadExit.code = 'DEAD_EXIT';
deadExit.severity = 'error';

const unreachable = (state, ctx) => {
  if (ctx.reachable.has(state.name)) return null;
  return issue(
    unreachable,
    state,
    'Nothing exits to this state and it is not the initial state, so no conversation can ever arrive here. Whatever it does is dead weight in the document until some other state names it as an exit.'
  );
};
unreachable.code = 'UNREACHABLE';
unreachable.severity = 'drift';

const undispatchable = (state, ctx) => {
  const bad = (state.tools || []).filter((name) => {
    const tool = ctx.toolsByName[name];
    return tool && tool.dispatchable === false;
  });
  if (bad.length === 0) return null;
  return issue(
    undispatchable,
    state,
    'This state lists ' + list(bad) + ', which the tool catalog marks dispatchable false. The dispatcher cannot invoke it, so the model will announce work it is physically unable to do.'
  );
};
undispatchable.code = 'UNDISPATCHABLE';
undispatchable.severity = 'error';

const untieredMoney = (state, ctx) => {
  const bad = (state.tools || []).filter((name) => {
    const tool = ctx.toolsByName[name];
    return tool && tool.money === true && (tool.tier === null || tool.tier === undefined);
  });
  if (bad.length === 0) return null;
  return issue(
    untieredMoney,
    state,
    list(bad) + ' moves real money and has no spend tier in the catalog. With a null tier there is no ceiling to check against, so the amount is whatever the conversation talked itself into. Assign a tier before this ships.'
  );
};
untieredMoney.code = 'UNTIERED_MONEY';
untieredMoney.severity = 'error';

const ghostArtifact = (state, ctx) => {
  if (!state.artifact) return null;
  const entry = ctx.artifactsByName[state.artifact];
  if (entry && entry.registered !== false) return null;
  const reason = entry
    ? 'is in the artifact catalog with registered false'
    : 'is not in the artifact catalog at all';
  return issue(
    ghostArtifact,
    state,
    'This state binds the artifact ' + state.artifact + ', which ' + reason + '. There is no card renderer bound to it, so the state does its work and the user sees an empty space where the result should be.'
  );
};
ghostArtifact.code = 'GHOST_ARTIFACT';
ghostArtifact.severity = 'drift';

// The one exported array. Order here is the order issues render in.
export const ISSUE_RULES = [
  noPrompt,
  haltNoAsk,
  deadExit,
  unreachable,
  undispatchable,
  untieredMoney,
  ghostArtifact,
];

// ---------------------------------------------------------------------------
// Runners
// ---------------------------------------------------------------------------

export const issuesForState = (state, ctx) =>
  ISSUE_RULES.map((rule) => rule(state, ctx)).filter(Boolean);

// Returns { byState: { [stateName]: issue[] }, all: issue[] }.
// An agent with no loaded states reports nothing rather than guessing; the
// left rail falls back to the lean issueCount for those.
export const issuesForAgent = (agent, toolCatalog, artifactCatalog) => {
  const states = (agent && Array.isArray(agent.states) ? agent.states : []);
  const ctx = buildIssueContext(agent, toolCatalog, artifactCatalog);
  const byState = {};
  const all = [];
  states.forEach((s) => {
    const found = issuesForState(s, ctx);
    byState[s.name] = found;
    found.forEach((f) => all.push(f));
  });
  return { byState, all };
};

export default ISSUE_RULES;
